/**
 * Section navigation on the three admin profile edit forms — venue, catering
 * and supplier. Each renders one `[data-magellan-expedition]` list in the
 * sidebar whose items carry `data-magellan-arrival="<name>"`, and an empty
 * anchor `[data-magellan-destination="<name>"]` above each section of the form.
 *
 * Two behaviours: the arrival for the section you are in gets `is-current`,
 * and clicking an arrival scrolls to its section instead of jumping.
 *
 * The sidebar's own stickiness is CSS plus `stickySidebar.ts` — nothing here
 * positions the expedition.
 */

import { delegate } from 'common/delegate';

const EXPEDITION_SELECTOR = '[data-magellan-expedition]';
const ARRIVAL_SELECTOR = '[data-magellan-arrival]';
const ARRIVAL_LINK_SELECTOR = `${ARRIVAL_SELECTOR} a[href*="#"]`;
const ACTIVE_CLASS = 'is-current';

let initialized = false;

export default function initializeMagellan(): void {
  if (initialized) return;
  initialized = true;

  if (!document.querySelector(EXPEDITION_SELECTOR)) return;

  delegate(document, 'click', ARRIVAL_LINK_SELECTOR, handleArrivalClick);
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);

  updateArrivals();
}

function destinationFor(name: string): HTMLElement | null {
  return document.querySelector(
    `[data-magellan-destination="${CSS.escape(name)}"]`
  );
}

/** Document-relative top, floored the way the section boundaries are compared. */
function documentTop(element: HTMLElement): number {
  return Math.floor(element.getBoundingClientRect().top + window.scrollY);
}

let pending = false;

function scheduleUpdate(): void {
  if (pending) return;
  pending = true;
  window.requestAnimationFrame(() => {
    pending = false;
    updateArrivals();
  });
}

function updateArrivals(): void {
  document.querySelectorAll(EXPEDITION_SELECTOR).forEach(expedition => {
    const arrivals = Array.from(
      expedition.querySelectorAll<HTMLElement>(ARRIVAL_SELECTOR)
    );

    // Ordered by where the sections actually sit rather than by their order in
    // the list, so the deepest section scrolled past is the one that wins even
    // if the markup ever disagrees with the layout.
    const passed = arrivals
      .map(arrival => {
        const name = arrival.getAttribute('data-magellan-arrival');
        const destination = name ? destinationFor(name) : null;
        return destination ? { arrival, top: documentTop(destination) } : null;
      })
      .filter(
        (entry): entry is { arrival: HTMLElement; top: number } =>
          entry !== null && window.scrollY >= entry.top
      )
      .sort((a, b) => a.top - b.top);

    // Above the first section nothing is current.
    const current = passed.length ? passed[passed.length - 1].arrival : null;

    arrivals.forEach(arrival => {
      arrival.classList.toggle(ACTIVE_CLASS, arrival === current);
    });
  });
}

function handleArrivalClick(event: MouseEvent, link: Element): void {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(link instanceof HTMLAnchorElement)) return;

  // Only in-page links: one pointing at another document has to navigate.
  const samePage =
    link.hostname === window.location.hostname &&
    link.pathname === window.location.pathname;
  const name = link.hash.slice(1);
  if (!samePage || !name) return;

  const destination = destinationFor(name);
  if (!destination) return;

  event.preventDefault();

  // A user agent is allowed to ignore scroll behaviour, so a reader who asks
  // for reduced motion is only served by dropping the animation here.
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  window.scrollTo({
    top: documentTop(destination),
    behavior: prefersReducedMotion ? 'auto' : 'smooth'
  });

  // Assigning `location.hash` would jump the page and undo the animation.
  // The query is part of the link's own address -- a fragment-only href
  // resolves against the current URL -- so rebuilding without it would strip
  // whatever the page was reached with.
  window.history.pushState(null, '', `${link.pathname}${link.search}#${name}`);
}
